<template>
	<div v-if="isMobile" class="vcx-nav-logo-mobile vcx-flex vcx-justify-center">
		<BaseLink :link="vcxLandingLink">
			<BaseSvgIcon name="fundrise-vcx-white" width="167px" height="30px" title="VCX by Fundrise" />
		</BaseLink>
	</div>

	<nav class="vcx-nav" :class="{ 'vcx-nav--mobile': isMobile }" aria-label="Main navigation" data-context="vcx-nav">
		<div class="vcx-nav__container">
			<BaseLink v-if="!isMobile" :link="vcxLandingLink" class="vcx-nav__logo">
				<BaseSvgIcon name="fundrise-vcx-white" width="167px" height="36px" title="VCX by Fundrise" />
			</BaseLink>
			<div class="vcx-nav__menu">
				<BaseLink
					v-for="link in navLinks"
					:id="`nav-${link.hash}`"
					:key="link.hash"
					:link="link"
					classes="vcx-nav__link"
					@click="handleNavClick($event, link.hash)"
				/>
				<BaseButton
					classes="vcx-button-primary button vcx-px-100-smo vcx-py-25-smo"
					data-test="vcx-flyout-button"
					@click="showFlyOutMenu"
					>Learn more</BaseButton
				>
			</div>
		</div>
	</nav>
	<!-- Flyout Menu -->
	<VcxFlyOut v-model="showFlyOut" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import VcxFlyOut from '@components/vcx/vcx-fly-out.vue';
import { vcxLandingLink } from '@constants/vcx';
import { useAppStore } from '@stores/app';
import { Link } from 'types/layout';

const router = useRouter();
const route = useRoute();

const appStore = useAppStore();

const isMobile = computed(() => appStore.isMobile);

const showFlyOut = ref(false);

const navLinks: Array<Link> = [
	{ href: '#about-vcx', text: 'About VCX', hash: '#about-vcx' },
	{ href: '#performance', text: 'Performance', hash: '#performance' }
];

function handleNavClick(event: Event, hash: string | undefined): void {
	event.preventDefault();
	if (!hash) return;

	if (route.name !== 'vcx-landing') {
		router.push({ name: 'vcx-landing', hash });
	} else {
		router.replace({ hash });
	}
}

function showFlyOutMenu() {
	showFlyOut.value = true;
}

onMounted(() => {
	if (route.query.invest === 'true') {
		showFlyOutMenu();
	}
});
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive.scss' as *;

.vcx-nav-logo-mobile {
	background-color: var(--vcx-bg-dark);
	padding: var(--vcx-space-150) var(--vcx-space-150) 0 var(--vcx-space-150);
}

.vcx-nav {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	backdrop-filter: blur(32px);
	transition:
		background-color var(--vcx-transition-base),
		box-shadow var(--vcx-transition-base);

	&--mobile {
		position: sticky;
		backdrop-filter: none;
		background-color: var(--vcx-bg-dark);
		border-bottom: 1px solid var(--vcx-gray-70);
	}

	&__container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: var(--vcx-max-width);
		margin: 0 auto;
		padding: var(--vcx-space-150) var(--vcx-space-300);

		.vcx-nav--mobile & {
			padding: var(--vcx-space-100) var(--vcx-space-100);
		}
	}

	&__menu {
		display: flex;
		align-items: center;
		gap: var(--vcx-space-200);

		.vcx-nav--mobile & {
			width: 100%;
			gap: var(--vcx-space-50);
			justify-content: space-between;
		}
	}

	&__link {
		font-size: var(--vcx-font-size-body-lg);
		font-weight: var(--vcx-font-weight-regular);
		line-height: var(--vcx-line-height-body-lg);
		color: var(--vcx-gray-white);
		text-decoration: none;
		transition:
			opacity var(--vcx-transition-fast),
			color var(--vcx-transition-base),
			transform var(--vcx-transition-base);

		&:hover {
			transform: translateY(-2px);
			opacity: 0.8;
		}

		.vcx-nav--mobile & {
			font-size: var(--vcx-font-size-body-md);
		}
	}

	&--scrolled {
		background-color: var(--vcx-bg-dark);
		box-shadow: var(--vcx-shadow-md);
	}

	.vcx-button-primary {
		height: auto;

		@include vcx-media-smo {
			font-size: var(--vcx-font-size-body-sm);
		}

		&:focus,
		&:focus-within {
			background-color: var(--vcx-brand-primary);
			color: var(--vcx-content-primary);
		}

		&:hover:not(:disabled) {
			background-color: rgba(244, 99, 58, 0.75);
			color: var(--vcx-content-primary);
		}

		&:focus-visible {
			outline: 2px solid var(--vcx-brand-primary);
			outline-offset: 2px;
			background-color: var(--vcx-brand-primary);
			color: var(--vcx-content-primary);
		}

		.vcx-nav--mobile & {
			font-size: var(--vcx-font-size-body-lg);
		}
	}
}
</style>
